import { useState, useEffect } from "react";
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Textarea, Box, Typography } from "@mui/joy";
import { Button } from "../../shared";
import { X } from "lucide-react";

interface ImageUploadModalProps {
    open: boolean;
    files: File[];
    onClose: () => void;
    onUpload: (files: File[], note: string) => void;
}

export function ImageUploadModal({ open, files, onClose, onUpload }: ImageUploadModalProps) {
    const [note, setNote] = useState("");
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);

    useEffect(() => {
        if (files.length > 0) {
            // Create preview URLs for all files
            const urls = files.map((file) => URL.createObjectURL(file));
            setPreviewUrls(urls);

            // Cleanup function
            return () => {
                urls.forEach((url) => URL.revokeObjectURL(url));
            };
        }
    }, [files]);

    const handleUpload = () => {
        onUpload(files, note);
        setNote("");
        onClose();
    };

    const handleClose = () => {
        // Cleanup preview URLs
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        setNote("");
        setPreviewUrls([]);
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog layout="center" variant="soft" sx={{ maxWidth: "600px", width: "90%" }}>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography level="title-md">
                            Upload {files.length > 1 ? `${files.length} Images` : "Image"}
                        </Typography>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={handleClose}
                            sx={{ minHeight: "auto", p: 0.5 }}
                        >
                            <X size={18} />
                        </Button>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Image Preview(s) */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                maxHeight: "300px",
                                overflowY: "auto",
                                p: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "8px",
                            }}
                        >
                            {previewUrls.map((url, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        width: files.length === 1 ? "100%" : "150px",
                                        height: files.length === 1 ? "auto" : "150px",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <img
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        style={{
                                            width: "100%",
                                            height: files.length === 1 ? "auto" : "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>

                        {/* Note Input */}
                        <Box>
                            <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>
                                Add a note (optional)
                            </Typography>
                            <Textarea
                                placeholder="Enter notes about this image..."
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                minRows={3}
                                maxRows={6}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" color="neutral" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="soft" onClick={handleUpload}>
                        Upload
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
}
